import { DealStateEnum } from '../gql/dealGql';
import {
  DealMediaCustomerTypeEnum,
  DealMediaNoSignatureUploadTypeEnum,
  DealMediaReleaseOfInterestTypeEnum,
  DealMediaTypeEnum,
  Maybe,
  ProductSlug,
  TtDocumentName,
} from '../gql/generated/graphql';

import { CustomDates } from '../types/dealDates';
import { toDealMediaTypeArray } from '../utils/mediaType';

// Abbreviations to improve readability
const TTDN = TtDocumentName;
const DMT = DealMediaTypeEnum;

type SharedTtDocumentName =
  | TtDocumentName.PowerOfAttorney
  | TtDocumentName.ProcessorPowerOfAttorney
  | TtDocumentName.PurchaseOrder
  | TtDocumentName.OdometerStatement
  | TtDocumentName.GmLeasePacket
  | TtDocumentName.GmAssignmentOfTitleDamageDisclosure
  | TtDocumentName.UsBankSpecificAlabamaSpecificAirbagDisclosure
  | TtDocumentName.UsBankSpecificIllinoisSpecificLeasedVehicleRegistrationAffirmation
  | TtDocumentName.PorscheSpecificPayoffInstructions
  | TtDocumentName.LaSpecificMercedesSpecificFloodDamageDisclosure;

type PayoffTtDocumentName =
  | TtDocumentName.ToyotaSpecificPowerOfAttorney
  | TtDocumentName.LexusSpecificPowerOfAttorney
  | TtDocumentName.TeslaSpecificDocumentPacket
  | TtDocumentName.TaterDocumentPacket
  | TtDocumentName.LeaseEndPayoffAuthorization
  | TtDocumentName.LienholderSpecificBillOfSale
  | TtDocumentName.LienholderSpecificOdometerStatement;

type TitleRegistrationTtDocumentName = Exclude<
  TtDocumentName,
  PayoffTtDocumentName | SharedTtDocumentName
>;

export const TitleRegistrationDocumentTypeMap: Record<
  TitleRegistrationTtDocumentName | SharedTtDocumentName,
  DealMediaTypeEnum
> = {
  [TTDN.BackOfDriversLicense]: DMT.BackOfDriversLicense,
  [TTDN.CobuyerBackOfDriversLicense]: DMT.BackOfCoBuyersLicense,
  [TTDN.CobuyerDriversLicense]: DMT.FrontOfCoBuyersLicense,
  [TTDN.DriversLicense]: DMT.FrontOfDriversLicense,
  [TTDN.Emissions]: DMT.Emissions,
  [TTDN.Odometer]: DMT.OdoDoc,
  [TTDN.HondaSpecificOdometerStatement]: DMT.HondaSpecificOdometerDoc,
  [TTDN.AcuraSpecificOdometerStatement]: DMT.AcuraSpecificOdometerDoc,
  [TTDN.ProofOfInsurance]: DMT.FrontOfInsuranceCard,
  [TTDN.ProofOfResidence]: DMT.ProofOfResidence,
  [TTDN.ProofOfResidence2]: DMT.ProofOfResidence2,
  [TTDN.PropertyTaxReceipt]: DMT.PropertyTaxReceipt,
  [TTDN.Registration]: DMT.Registration,
  [TTDN.VinInspection]: DMT.VinInspection,
  [TTDN.SafetyInspection]: DMT.SafetyInspection,
  [TTDN.StateSpecificPowerOfAttorney]: DMT.StateSpecificPowerOfAttorney,
  [TTDN.SecurePowerOfAttorney]: DMT.SecurePowerOfAttorney,
  [TTDN.BillOfSale]: DMT.PurchaseOrder,
  [TTDN.StateSpecificBillOfSale]: DMT.StateSpecificPurchaseOrder,
  [TTDN.CobuyerOdometerStatement]: DMT.LeaseendOdoDocCobuyer,
  [TTDN.JointOdometerStatement]: DMT.LeaseendOdoDocJoint,
  [TTDN.StateSpecificOdometerStatement]: DMT.StateSpecificOdoDoc,
  [TTDN.CobuyerStateSpecificOdometerStatement]: DMT.CobuyerStateSpecificOdoDoc,
  [TTDN.TitleApp]: DMT.TitleApplication,
  [TTDN.StateSpecificDamageDisclosure]: DMT.StateSpecificDamageDisclosure,
  [TTDN.RegistrationSpecificPowerOfAttorney]: DMT.RegistrationSpecificPowerOfAttorney,
  [TTDN.CobuyerPowerOfAttorney]: DMT.PowerOfAttorneyCobuyer,
  [TTDN.CobuyerStateSpecificPowerOfAttorney]: DMT.StateSpecificPowerOfAttorneyCobuyer,
  [TTDN.CobuyerRegistrationSpecificPowerOfAttorney]: DMT.CobuyerRegistrationSpecificPowerOfAttorney,
  [TTDN.CobuyerSecurePowerOfAttorney]: DMT.CobuyerSecurePowerOfAttorney,
  [TTDN.SalesTaxExemption]: DMT.SalesTaxExemption,
  [TTDN.OhioSpecificHondaSpecificPowerOfAttorney]: DMT.StateAndLienholderSpecificPowerOfAttorney,
  [TTDN.CobuyerJointTenancyWithRightsOfSurvivorship]: DMT.ColoradoJointTenancy,
  [TTDN.PlateTransferLetter]: DMT.PlateTransferLetter,
  [TTDN.ElectronicSignatureAttestation]: DMT.ElectronicSignatureAttestation,
  [TTDN.ElectronicSignatureCertification]: DMT.ElectronicSignatureCertification,
  [TTDN.LicensePlate]: DMT.LicensePlate,
  [TTDN.IdahoSalesTaxExemption]: DMT.IdahoSalesTaxExemption,
  [TTDN.PorscheSpecificOdometerStatement]: DMT.PorscheSpecificOdometerDoc,
  [TTDN.OhioSpecificHondaSpecificOdometerStatement]: DMT.OhioHondaSpecificOdometerDoc,
  [TTDN.ApplicationForCopyOfRegistration]: DMT.NewHampshireRegistrationCopyApplication,
  [TTDN.CaliforniaSpecialInterestLicensePlateApplication]:
    DMT.CaliforniaSpecialInterestLicensePlateApplication,
  [TTDN.SmogInspection]: DMT.SmogInspection,
  [TTDN.SupplementalSecureAndVerifiableIdentificationInformationAndAttestationClause]:
    DMT.ColoradoSupplementalIdentificationClause,
  [TTDN.CobuyerSupplementalSecureAndVerifiableIdentificationInformationAndAttestationClause]:
    DMT.ColoradoCobuyerSupplementalIdentificationClause,
  [TTDN.ElectronicRegistrationAndTitleAgreement]:
    DMT.ConnecticutElectronicRegistrationAndTitleAgreement,
  [TTDN.CobuyerElectronicRegistrationAndTitleAgreement]:
    DMT.CoBuyerConnecticutElectronicRegistrationAndTitleAgreement,
  [TTDN.ProcessorPowerOfAttorney]: DMT.ProcessorPowerOfAttorney,
  [TTDN.CobuyerProcessorPowerOfAttorney]: DMT.CobuyerProcessorPowerOfAttorney,

  // Shared document types.
  [TTDN.PowerOfAttorney]: DMT.PowerOfAttorney,
  [TTDN.PurchaseOrder]: DMT.PurchaseOrder,
  [TTDN.OdometerStatement]: DMT.LeaseendOdoDoc,
  [TTDN.GmLeasePacket]: DMT.GmLeasePacket,
  [TTDN.GmAssignmentOfTitleDamageDisclosure]: DMT.GmAssignmentOfTitleDamageDisclosure,
  [TTDN.UsBankSpecificAlabamaSpecificAirbagDisclosure]: DMT.AlabamaAirbagDisclosureStatement,
  [TTDN.UsBankSpecificIllinoisSpecificLeasedVehicleRegistrationAffirmation]:
    DMT.IllinoisLeasedVehicleRegistrationAffirmation,
  [TTDN.PorscheSpecificPayoffInstructions]: DMT.PorscheSpecificPayoffInstructions,
  [TTDN.LaSpecificMercedesSpecificFloodDamageDisclosure]: DMT.LouisianaMercedesDamageDisclosure,
};

export const PayoffDocumentTypeMap: Record<
  PayoffTtDocumentName | SharedTtDocumentName,
  DealMediaTypeEnum
> = {
  [TTDN.ToyotaSpecificPowerOfAttorney]: DMT.RoiToyotaSpecificPowerOfAttorney,
  [TTDN.LexusSpecificPowerOfAttorney]: DMT.RoiLexusSpecificPowerOfAttorney,
  [TTDN.TeslaSpecificDocumentPacket]: DMT.RoiTeslaSpecificDocumentPacket,
  [TTDN.TaterDocumentPacket]: DMT.RoiTaterDocumentPacket,
  [TTDN.LeaseEndPayoffAuthorization]: DMT.RoiLeaseEndPayoffAuthorization,
  [TTDN.LienholderSpecificBillOfSale]: DMT.RoiLienholderSpecificBillOfSale,
  [TTDN.LienholderSpecificOdometerStatement]: DMT.RoiLienholderSpecificOdometerStatement,

  // Shared document types.
  [TTDN.PowerOfAttorney]: DMT.RoiPowerOfAttorney,
  [TTDN.ProcessorPowerOfAttorney]: DMT.RoiProcessorPowerOfAttorney,
  [TTDN.PurchaseOrder]: DMT.RoiLeaseEndPurchaseOrder,
  [TTDN.OdometerStatement]: DMT.RoiLeaseEndOdometerStatement,
  [TTDN.GmLeasePacket]: DMT.RoiGmLeasePacket,
  [TTDN.GmAssignmentOfTitleDamageDisclosure]: DMT.RoiGmAssignmentOfTitleDamageDisclosure,
  [TTDN.UsBankSpecificAlabamaSpecificAirbagDisclosure]:
    DMT.RoiUsbankSpecificAlabamaSpecificAirbagDisclosure,
  [TTDN.UsBankSpecificIllinoisSpecificLeasedVehicleRegistrationAffirmation]:
    DMT.RoiUsbankSpecificIllinoisSpecificLeasedVehicleRegistrationAffirmation,
  [TTDN.PorscheSpecificPayoffInstructions]: DMT.RoiPorscheSpecificPayoffInstructions,
  [TTDN.LaSpecificMercedesSpecificFloodDamageDisclosure]: DMT.LouisianaMercedesDamageDisclosure,
};

// keep in sync with the backend
export const DealMediaTypeLabels: Record<DealMediaTypeEnum, string> = {
  [DMT.RateExceptionForm]: 'Rate Exception Form',
  [DMT.PicturesOfVehicle]: 'Pictures of Vehicle',
  [DMT.OdometerPicture]: 'Odometer Picture',
  [DMT.FrontOfDriversLicense]: "Driver's License - Front",
  [DMT.BackOfDriversLicense]: "Driver's License - Back",
  [DMT.FrontOfInsuranceCard]: 'Insurance Card - Front',
  [DMT.BackOfInsuranceCard]: 'Insurance Card - Back',
  [DMT.Registration]: 'Registration',
  [DMT.LeaseAgreement]: 'Lease Agreement',
  [DMT.OdoDoc]: 'Odo Doc',
  [DMT.LeaseendOdoDoc]: 'Lease End Odo',
  [DMT.LeaseendOdoDocCobuyer]: 'Lease End Odo (Co-buyer)',
  [DMT.LeaseendOdoDocJoint]: 'Lease End Odo (Joint)',
  [DMT.GmLeasePacket]: 'GM Lease Packet',
  [DMT.GmAssignmentOfTitleDamageDisclosure]: 'GM Assignment of Title Damage Disclosure',
  [DMT.HondaSpecificOdometerDoc]: 'Honda Specific Odo Doc',
  [DMT.AcuraSpecificOdometerDoc]: 'Acura Specific Odo Doc',
  [DMT.StateSpecificOdoDoc]: 'State Specific Odo Doc',
  [DMT.CobuyerStateSpecificOdoDoc]: 'State Specific Odo Doc (Co-buyer)',
  [DMT.VinInspection]: 'VIN Inspection',
  [DMT.SafetyInspection]: 'Safety Inspection',
  [DMT.BuyersGuide]: "Buyer's Guide",
  [DMT.TaterDocs]: 'Tater Docs',
  [DMT.TaxExemption]: 'Tax Exemption',
  [DMT.PowerOfAttorney]: 'Power of Attorney',
  [DMT.StateSpecificPowerOfAttorney]: 'State Specific Power of Attorney',
  [DMT.PowerOfAttorneyCobuyer]: 'Power of Attorney (Co-buyer)',
  [DMT.StateSpecificPowerOfAttorneyCobuyer]: 'State Specific Power of Attorney (Co-buyer)',
  [DMT.SecurePowerOfAttorney]: 'Secure Power of Attorney',
  [DMT.StateSpecificDocs]: 'State Specific Docs',
  [DMT.PlateTransferLetter]: 'Plate Transfer Letter',
  [DMT.Contract]: 'Contract',
  [DMT.Title]: 'Title',
  [DMT.TitleApplication]: 'Title Application',
  [DMT.TitlePacket]: 'Title Packet',
  [DMT.TitleRelease]: 'Title Release',
  [DMT.CreditApproval]: 'Credit Approval',
  [DMT.ReassignmentDisclosure]: 'Reassignment Disclosure',
  [DMT.PayoffAuthorization]: 'Payoff Authorization',
  [DMT.PurchaseOrder]: 'Purchase Order',
  [DMT.StateSpecificPurchaseOrder]: 'State Specific Purchase Order',
  [DMT.LeaseEndCashback]: 'Lease End Cashback',
  [DMT.DamageDisclosure]: 'Damage Disclosure',
  [DMT.StateSpecificDamageDisclosure]: 'State Specific Damage Disclosure',
  [DMT.TexasHidalgoDoc]: 'Texas Hidalgo Doc',
  [DMT.AgreementToFurnishInsurance]: 'Agreement to Furnish Insurance',
  [DMT.LeaseEndAcqContract]: 'Lease End Acq Contract',
  [DMT.BookSheet]: 'Book Sheet',
  [DMT.RegistrationComplete]: 'Registration/Title - Complete',
  [DMT.R1EContract]: 'R1 E-Contract',
  [DMT.MembershipForm]: 'Membership Form',
  [DMT.ESignConsent]: 'E-Sign Consent',
  [DMT.PrivacyPolicy]: 'Privacy Policy',
  [DMT.GlbaPrivacyNotice]: 'GLBA Privacy Notice',
  [DMT.Other]: 'Other',
  [DMT.FrontOfCoBuyersLicense]: "Driver's License (Co-Buyer) - Front",
  [DMT.BackOfCoBuyersLicense]: "Driver's License (Co-Buyer) - Back",
  [DMT.FrontOfCoBuyersInsurance]: 'Insurance (Co-Buyer) - Front',
  [DMT.BackOfCoBuyersInsurance]: 'Insurance (Co-buyer) - Back',
  [DMT.Emissions]: 'Emissions',
  [DMT.PropertyTaxReceipt]: 'Property Tax Receipt',
  [DMT.ProofOfResidence]: 'Proof of Residence',
  [DMT.ProofOfResidence2]: 'Proof of Residence 2',
  [DMT.RegistrationSpecificPowerOfAttorney]: 'Registration Specific Power of Attorney',
  [DMT.CobuyerSecurePowerOfAttorney]: 'Secure Power of Attorney (Co-buyer)',
  [DMT.PhysicalSignatureCoverLetter]: 'Physical Signature Cover Letter',
  [DMT.CreditApplication]: 'Credit Application',
  [DMT.PayoffDocs]: 'Payoff Docs',
  [DMT.PayoffPacket]: 'Payoff Packet',
  [DMT.ProofOfIncome]: 'Proof of Income',
  [DMT.DownPayment]: 'Down Payment',
  [DMT.SignedDocs]: 'Signed Docs',
  [DMT.SalesTaxExemption]: 'Sales Tax Exemption',
  [DMT.VscAndGap]: 'VSC & GAP',
  [DMT.DemandLetter]: 'Demand Letter',
  [DMT.ColoradoSupplementalIdentificationClause]: 'Colorado Supplemental Identification',
  [DMT.ColoradoJointTenancy]: 'Colorado Joint Tenancy Form',
  [DMT.CaliforniaStatementOfFacts]: 'California Statement of Facts',
  [DMT.CaliforniaStatementOfFactsCobuyer]: 'California Statement of Facts (Co-buyer)',
  [DMT.ElectronicSignatureVerification]: 'Electronic Signature Verification',
  [DMT.ElectronicSignatureAttestation]: 'Electronic Signature Attestation',
  [DMT.ElectronicSignatureCertification]: 'Electronic Signature Certification',
  [DMT.LicensePlate]: 'License Plate',
  [DMT.TempTag]: 'Temp Tag',
  [DMT.IdahoSalesTaxExemption]: 'Idaho Sales Tax Exemption',
  [DMT.PorscheSpecificOdometerDoc]: 'Porsche Specific Odo Doc',
  [DMT.PorscheSpecificPayoffInstructions]: 'Porsche Specific Payoff Instructions',
  [DMT.WetSignature]: 'Wet Signature',
  [DMT.AlabamaAirbagDisclosureStatement]: 'Alabama Airbag Disclosure Statement',
  [DMT.LouisianaMercedesDamageDisclosure]: 'Louisiana Mercedes Damage Disclosure',
  [DMT.ReleaseOfInterest]: 'Release of Interest',
  [DMT.OhioHondaSpecificOdometerDoc]: 'Ohio Honda Specific Odo Doc',
  [DMT.NewHampshireRegistrationCopyApplication]: 'New Hampshire Registration Copy Application',
  [DMT.CaliforniaSpecialInterestLicensePlateApplication]:
    'California Special Interest License Plate Application',
  [DMT.SmogInspection]: 'Smog Inspection',
  [DMT.ColoradoCobuyerSupplementalIdentificationClause]:
    'Colorado Supplemental Identification (Co-buyer)',

  [DMT.ConnecticutElectronicRegistrationAndTitleAgreement]:
    'Connecticut Electronic Registration and Title Agreement',

  [DMT.CoBuyerConnecticutElectronicRegistrationAndTitleAgreement]:
    'Connecticut Electronic Registration and Title Agreement (Co-buyer)',

  [DMT.IllinoisLeasedVehicleRegistrationAffirmation]:
    'Illinois Leased Vehicle Registration Affirmation',

  [DMT.CobuyerRegistrationSpecificPowerOfAttorney]:
    'Registration Specific Power of Attorney (Co-buyer)',

  [DMT.StateAndLienholderSpecificPowerOfAttorney]:
    'State and Lienholder Specific Power of Attorney',

  [DMT.ProcessorPowerOfAttorney]: 'Processor Power of Attorney',
  [DMT.CobuyerProcessorPowerOfAttorney]: 'Processor Power of Attorney (Co-buyer)',

  // Release of Interest/Payoff product document types.
  [DMT.RoiPowerOfAttorney]: 'ROI Power of Attorney',
  [DMT.RoiProcessorPowerOfAttorney]: 'ROI Processor Power of Attorney',
  [DMT.RoiToyotaSpecificPowerOfAttorney]: 'ROI Toyota Specific Power of Attorney',
  [DMT.RoiLexusSpecificPowerOfAttorney]: 'ROI Lexus Specific Power of Attorney',
  [DMT.RoiLeaseEndPurchaseOrder]: 'ROI Lease End Purchase Order',
  [DMT.RoiGmLeasePacket]: 'ROI GM Lease Packet',
  [DMT.RoiGmAssignmentOfTitleDamageDisclosure]: 'ROI GM Assignment of Title Damage Disclosure',
  [DMT.RoiTeslaSpecificDocumentPacket]: 'ROI Tesla Specific Document Packet',
  [DMT.RoiTaterDocumentPacket]: 'ROI Tater Document Packet',
  [DMT.RoiLeaseEndPayoffAuthorization]: 'ROI Lease End Payoff Authorization',
  [DMT.RoiLienholderSpecificBillOfSale]: 'ROI Lienholder Specific Bill of Sale',
  [DMT.RoiLeaseEndOdometerStatement]: 'ROI Lease End Odometer Statement',
  [DMT.RoiLienholderSpecificOdometerStatement]: 'ROI Lienholder Specific Odometer Statement',
  [DMT.RoiPorscheSpecificPayoffInstructions]: 'ROI Porsche Specific Payoff Instructions',
  [DMT.RoiUsbankSpecificAlabamaSpecificAirbagDisclosure]:
    'ROI USBank Specific Alabama Specific Airbag Disclosure',
  [DMT.RoiUsbankSpecificIllinoisSpecificLeasedVehicleRegistrationAffirmation]:
    'ROI USBank Specific Illinois Specific Leased Vehicle Registration Affirmation',
  [DMT.SantanderGuaranteeOfTitle]: 'Santander Guarantee of Title',
  [DMT.SantanderCanadaConditionalSalesContract]: 'Santander Canada Conditional Sales Contract',
  [DMT.SantanderCanadaCreditApplication]: 'Santander Canada Credit Application',
  [DMT.SantanderCanadaCustomerIdentificationForm]: 'Santander Canada Customer Identification Form',
  [DMT.CpsGuaranteeOfTitle]: 'Consumer Portfolio Services Guarantee of Title',
  [DMT.AllyCertifiedPreOwnedAcknowledgement]: 'Ally Certified Pre-Owned Acknowledgement',
  [DMT.MontrosePowerOfAttorney]: 'Montrose Limited Power of Attorney',
  [DMT.NewYorkCpsNoticeOfLien]: 'New York Consumer Portfolio Services Notice of Lien',
  [DMT.FirstFedOdometerDisclosure]: 'First Federal Odometer Disclosure',
  [DMT.FirstFedCoverLetter]: 'First Federal Cover Letter',
  [DMT.FirstFedPayoffAuthorization]: 'First Federal Payoff Authorization and Title Instructions',
};

// Used in the Media Form. We don't need the Wet Signature Sent Date there.
export const DealMediaTypeDateFieldMap: {
  [key in DealMediaTypeEnum]?: { field: keyof CustomDates; label: string };
} = {
  [DMT.Title]: {
    field: DealStateEnum.TitleReceived,
    label: 'Title Received Date',
  },
  [DMT.WetSignature]: {
    field: 'wet_sig_received_date',
    label: 'Wet Signature Received Date',
  },
};

export const DealMediaGroupOnlyTypes = [DMT.ElectronicSignatureVerification];
export const getDealMediaTypeLabel = (value: Maybe<DealMediaTypeEnum>) =>
  value ? DealMediaTypeLabels[value] : '';
export const DealMediaTypeOptions = Object.values(DealMediaTypeEnum)
  .filter((value) => !DealMediaGroupOnlyTypes.includes(value))
  .map((value) => ({
    value,
    label: getDealMediaTypeLabel(value),
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

export const DEAL_MEDIA_CUSTOMER_TYPES = toDealMediaTypeArray(DealMediaCustomerTypeEnum);

export const DEAL_MEDIA_CUSTOMER_TYPE_OPTIONS = DEAL_MEDIA_CUSTOMER_TYPES.map((value) => ({
  value,
  label: DealMediaTypeLabels[value],
})).sort((a, b) => a.label.localeCompare(b.label));

export const DEAL_MEDIA_RELEASE_OF_INTEREST_TYPES = toDealMediaTypeArray(
  DealMediaReleaseOfInterestTypeEnum,
);

export const DealMediaCustomerNoPhotoTypes = [
  DMT.Registration,
  DMT.VinInspection,
  DMT.SafetyInspection,
  DMT.Emissions,
  DMT.PlateTransferLetter,
];

const DEAL_MEDIA_NO_SIGNATURE_UPLOAD_TYPES = toDealMediaTypeArray(
  DealMediaNoSignatureUploadTypeEnum,
);

const PRODUCT_SLUG_VALUES: ProductSlug[] = Object.values(ProductSlug);

const getProductSlugFromKey = (key: string): ProductSlug | null => {
  // Key format: {dealId}/{uuid}__{LastName}_{productslug}.pdf
  const filename =
    key
      .split('__')
      .pop()
      ?.replace(/\.pdf$/i, '') ?? '';
  return PRODUCT_SLUG_VALUES.find((slug) => filename.endsWith(`_${slug}`)) ?? null;
};

export const isSignatureUploadable = (
  mediaType: DealMediaTypeEnum,
  selectedProductSlugs: ProductSlug[],
  key?: string | null,
) => {
  if (mediaType === DMT.VscAndGap) {
    if (!key) {
      return false;
    }
    const productSlug = getProductSlugFromKey(key);
    if (productSlug === null) {
      return false;
    }
    // Maintenance is always bundled with VSC, so treat it as selected when VSC is.
    if (productSlug === ProductSlug.Maintenance) {
      return selectedProductSlugs.includes(ProductSlug.Vsc);
    }
    return selectedProductSlugs.includes(productSlug);
  }

  return !DEAL_MEDIA_NO_SIGNATURE_UPLOAD_TYPES.includes(mediaType);
};

export enum MismatchSelectionEnum {
  Application = 'application',
  Document = 'document',
}

export enum DealMediaStatusEnum {
  All = 'all',
  Missing = 'missing',
  Verified = 'verified',
  Unverified = 'unverified',
  Uncategorized = 'uncategorized',
}
